/**
 * apPortProfile -- the pure logic behind the AP "Port Profiles" builder (routes/ApPortProfiles).
 *
 * Kept out of the React component so the rules an operator relies on -- what a profile's rows
 * are, and how a set of builder rows serializes into the stored `ports` map -- are pinned by
 * `npm run test:unit` (src/lib/apPortProfile.test.ts) instead of living inline where nothing
 * exercises them.
 *
 * THE BUG THIS FIXES (2026-09-15): the old builder let the operator type FREE TEXT into the port
 * field, so friendly names ("TV", "AppleTV") were stored as the port KEYS. An AP's real ports are
 * WAN* + LAN1..LAN4, so applying such a profile failed every time -- the keys matched no port.
 *
 * THE FIX: the port rows are now the AP's CANONICAL ports, which the operator cannot retype:
 *   - WAN*      the uplink trunk (fixed role Uplink; keeps the allowed-VLAN list from !147)
 *   - LAN1..LAN4 the wall-plate access ports (fixed role Access; each carries an editable
 *     friendly NAME stored as `label`, and binds to a site VLAN or a wired network)
 * The friendly name the operator types is the `label` (stored beside the binding), NOT the key.
 *
 * Reusable across models: a profile with LAN1-4 applied to an AP that has fewer LAN ports SOFT-
 * skips the missing ones on the backend (see services/port_profiles.apply_profile); the operator
 * builds one profile and it lands correctly on an EAP104 (4 LAN) or an EAP101 (1 LAN).
 *
 * The grammar for an uplink's Allowed-VLANs field is the switch port table's parseVlanList
 * (comma tokens + A-B ranges, deduped, 1-4094); the backend re-parses and is authoritative.
 */
import { parseVlanList } from "./portTable.ts";
import type { PortMapEntry, ApPortProfile } from "@/types/domain";

/** The uplink port key. A wall-plate AP reaches the controller over its WAN port; the `*`
 *  matches WAN1 on the device (uCentral logical names). */
export const UPLINK_PORT = "WAN*";
/** The wall-plate access ports, in faceplate order. A profile is reusable across models: an AP
 *  with fewer LAN ports simply skips the rows it lacks (backend soft-skip). */
export const CANONICAL_LAN_PORTS = ["LAN1", "LAN2", "LAN3", "LAN4"] as const;

/** A builder row's editable shape.
 *  - `port` is the CANONICAL key the row writes ("WAN*", "LAN1"), never typed by the operator.
 *  - `name` is the friendly wall-plate label ("TV"), stored as `label` beside the binding.
 *  - `target` chooses how an Access port binds: straight to a site VLAN (the owner's ask) or to
 *    a named wired network (kept for back-compat).
 *  - `allowed` is the uplink trunk allow-list as raw operator text.
 *  - `legacy` marks a row carried over from an older profile whose key is not in the canonical
 *    set (e.g. "LAN*"); it is shown read-only-key so editing an old profile never silently drops
 *    a mapping. */
export type DialogRow = {
  port: string;
  role: "uplink" | "access";
  name: string;
  target: "vlan" | "network";
  vlan: number | null;
  network_id: number | null;
  allowed: string;
  legacy: boolean;
};

/** The read-only port label shown on a row. "WAN*" -> "WAN", "LAN1" -> "LAN 1", a legacy glob
 *  like "LAN*" is shown verbatim (it IS the key). */
export function canonicalPortLabel(port: string): string {
  if (port.toUpperCase().startsWith("WAN")) return "WAN";
  const m = /^([A-Za-z]+)(\d+)$/.exec(port);
  return m ? `${m[1]} ${m[2]}` : port;
}

/** True when a port key is one of the canonical wall-plate ports (WAN* or LAN1..LAN4). */
function isCanonical(port: string): boolean {
  return port === UPLINK_PORT || (CANONICAL_LAN_PORTS as readonly string[]).includes(port);
}

/** A blank uplink row for the WAN port (fixed role Uplink). */
function blankUplinkRow(): DialogRow {
  return { port: UPLINK_PORT, role: "uplink", name: "", target: "vlan",
           vlan: null, network_id: null, allowed: "", legacy: false };
}

/** A blank access row for a canonical LAN port (fixed role Access, on the VLAN picker). */
function blankLanRow(port: string): DialogRow {
  return { port, role: "access", name: "", target: "vlan",
           vlan: null, network_id: null, allowed: "", legacy: false };
}

/** Fold a stored profile entry into a row (its binding, label and, for uplink, allow-list). */
function fillFromSpec(row: DialogRow, s: PortMapEntry): DialogRow {
  return {
    ...row,
    role: s.role,
    name: s.label ?? "",
    // A stored network_id means the legacy binding; otherwise default to the VLAN picker.
    target: s.network_id != null ? "network" : "vlan",
    vlan: s.vlan ?? null,
    network_id: s.network_id ?? null,
    allowed: (s.allowed_vlans ?? []).join(","),
  };
}

/** Build the builder's rows for a profile: the fixed canonical set (WAN* + LAN1..LAN4), with any
 *  existing bindings/labels folded in, plus one legacy row for any stored key that is NOT
 *  canonical (so editing an old profile -- {WAN*, LAN*} or free-text keys -- never drops it).
 *
 *  A brand-new profile (no `existing`) is the bare canonical set: WAN* as the uplink trunk and
 *  LAN1..LAN4 as empty access rows the operator names and binds. */
export function buildProfileRows(existing?: ApPortProfile): DialogRow[] {
  const stored = existing?.ports ?? {};
  const rows: DialogRow[] = [];

  // WAN uplink first (faceplate order); fold in its allow-list if the profile has one.
  const wan = blankUplinkRow();
  rows.push(stored[UPLINK_PORT] ? fillFromSpec(wan, stored[UPLINK_PORT]) : wan);

  // The four canonical LAN access ports.
  for (const port of CANONICAL_LAN_PORTS) {
    const base = blankLanRow(port);
    rows.push(stored[port] ? fillFromSpec(base, stored[port]) : base);
  }

  // Legacy keys (anything not canonical) carried over as read-only-key rows so a save round-trip
  // never silently drops an older profile's mapping.
  for (const [port, spec] of Object.entries(stored)) {
    if (isCanonical(port)) continue;
    const base: DialogRow = { port, role: spec.role, name: "", target: "vlan",
                              vlan: null, network_id: null, allowed: "", legacy: true };
    rows.push(fillFromSpec(base, spec));
  }
  return rows;
}

/** True when some uplink row's Allowed-VLANs text has a malformed token -- blocks Save (the
 *  same grammar the backend enforces, surfaced before the round-trip). */
export function hasInvalidAllowed(rows: DialogRow[]): boolean {
  return rows.some((r) => r.role === "uplink" && r.allowed.trim() !== ""
    && parseVlanList(r.allowed).invalid.length > 0);
}

/** Serialize builder rows into the stored `ports` map.
 *
 *  - Uplink (WAN*): always stored -- an Allowed-VLANs list (parsed, deduped) when given, else a
 *    plain trunk.
 *  - Access (LANn): stored only when the operator gave it SOMETHING -- a VLAN, a wired network,
 *    or a name. An entirely blank LAN row is dropped, so applying the profile never forces an
 *    untagged interface onto a wall port the operator left alone.
 *  - A non-empty `name` is written as `label` beside the binding.
 *  - Legacy rows are always kept (their key was already in a saved profile). */
export function rowsToPorts(rows: DialogRow[]): Record<string, PortMapEntry> {
  const ports: Record<string, PortMapEntry> = {};
  for (const row of rows) {
    const port = row.port.trim();
    if (!port) continue;

    if (row.role === "uplink") {
      const vlans = row.allowed.trim() ? parseVlanList(row.allowed).vlans : [];
      ports[port] = vlans.length ? { role: "uplink", allowed_vlans: vlans } : { role: "uplink" };
      continue;
    }

    const name = row.name.trim();
    const hasBinding = (row.target === "network" && row.network_id != null)
      || (row.target === "vlan" && row.vlan != null);
    // Drop an empty, unnamed, non-legacy access row -- nothing to map.
    if (!hasBinding && !name && !row.legacy) continue;

    let entry: PortMapEntry;
    if (row.target === "network" && row.network_id != null) {
      entry = { role: "access", network_id: row.network_id };
    } else if (row.target === "vlan" && row.vlan != null) {
      entry = { role: "access", vlan: row.vlan };
    } else {
      entry = { role: "access" }; // named-only / untagged access
    }
    if (name) entry.label = name;
    ports[port] = entry;
  }
  return ports;
}
